import type { MouseEvent} from 'react'
import { useCallback, useState , useEffect} from 'react'

import slugify from 'slugify'

import Slider, {
    SliderSizes,
    SliderVariants
} from '@gotamedia/fluffy/components/Slider'
import useSearchParamValues from "~/hooks/useSearchParamValues"
import useViewSize from '~/hooks/useViewSize'
import type { CompanyPayload } from '~/modules/marketGuide/types'
import * as Styled from './style'
import type * as Types from './types'

import { useLoaderData } from '@remix-run/react'
import type { SearchResponse } from '~/modules/marketGuide/types'
import { ItemType } from '~/services/resolvers/types'
import NoResults from '../NoResults/NoResults'


const MarketGuideWidgetCards: Types.MarketGuideCardComponet = props => {
    const { legacy } = useSearchParamValues(['legacy'])
    const legacyMode = legacy === "true"

    const [index, setIndex] = useState(0)
    const [clientX, setClientX] = useState(0)
    const [hover, setHover] = useState(false)

    const data = useLoaderData<SearchResponse>()
    const { isSmall, isMedium, isLarge } = useViewSize()


    const {
        width,
    } = props

    const chunk = (arr:CompanyPayload[] , size: number) =>
        Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
            arr.slice(i * size, i * size + size)
        )

    const chunksize = width && width > 0 ? Math.floor(width / 190) : isLarge() ? 4 : !isSmall() ? 3 : 2
    const height = legacyMode ? isSmall() ? 180 : 210 : isSmall() ? 216 : 196

    const [shuffledItems, setShuffledItems]  = useState<CompanyPayload[]>([]);
    
    useEffect(() => {
        if(typeof window === "undefined") return;
        if(data.payload.length === 0) return;


        const key = "shuffledItemsOrder";
        const saved = sessionStorage.getItem(key);

        saved ? 
            (() => {
                const order = JSON.parse(saved);
                const mapped = order.map((uuid: string) => data.payload.find(item => item.uuid === uuid)).filter(Boolean);
                const added = data.payload.filter(item => !order.includes(item.uuid));
                setShuffledItems([...mapped, ...added]);
            })() 
            : (() => { 
                const shuffled = [...data.payload].sort(() => Math.random() - 0.5);
                sessionStorage.setItem(key, JSON.stringify(shuffled.map(i => i.uuid)));
                setShuffledItems(shuffled);
            })();   
    }, [data.payload]);

    const result = shuffledItems ? chunk(shuffledItems, chunksize) : []

    const updateClientX = useCallback((clientX: number) => {
        setClientX(clientX)
    }, [])

    const updateIndex = useCallback((newIndex: number) => {
        setIndex(newIndex)
    }, [])

    const dragOrClick = useCallback((event: MouseEvent, companyName: string, uuid: string ) => {
        if (event.clientX < clientX + 5 && event.clientX > clientX - 5) {
            window.open(`${companyName ? `/${companyName}` : "" }/${uuid}`)
        }
    }, [clientX])

    return (
        <Styled.SliderContainer>
            {result.length > 0 ? (
                <Styled.Slider
                    legacy={legacyMode}
                    width={width || 0}
                    index={index}
                    variant={SliderVariants.Contrast}
                    size={SliderSizes.Small}
                    onChange={(newIndex: number) => updateIndex(newIndex)}
                    onMouseDown={(e: MouseEvent) => updateClientX(e.clientX)}
                    loop={false}
                    autoWidth
                    slidesCount={result.length}
                    shouldAutoPlay={!hover}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    dynamicBullets={!isLarge()}
                >
                    <Slider.Slides>
                        {result?.map((item, idx) => {
                            return (
                                <div key={idx}>
                                    <Styled.CardContainer>
                                        {
                                            item.map((it, idx) => {
                                                const companyName = slugify(it.name || 'företag', {
                                                    locale: 'sv'
                                                })

                                                return (
                                                    <Styled.CardWrapper
                                                        legacy={legacyMode}
                                                        onMouseUp={(e) => dragOrClick(e, companyName, it.uuid)}
                                                        key={`${it.uuid}-${idx}`}
                                                        width={width ? (((width-chunksize-44) / chunksize)-10) : undefined}
                                                    >
                                                        <Styled.Wrapper>
                                                            <Styled.CardImage src={it.logoUrl} legacy={legacyMode}>
                                                            </Styled.CardImage>
                                                        </Styled.Wrapper>
                                                        <Styled.CardName>
                                                            {it.name}
                                                        </Styled.CardName>
                                                    </Styled.CardWrapper>
                                                )
                                            })

                                        }
                                    </Styled.CardContainer>
                                </div>
                            )
                        })}
                    </Slider.Slides>
                    {
                        data.payload?.length > chunksize ? (
                            <>
                                <Styled.Slider.SliderBullets />

                                {
                                    (isLarge() || isMedium() && !legacyMode) && hover ? (
                                        <Styled.Slider.Navigation />
                                    ) : (
                                        null
                                    )
                                }
                            </>
                        ) : (
                            null
                        )
                    }
                </Styled.Slider>
            ) : (
                <NoResults heigth={height} type={ItemType.Company} width={width}/>
            )}

        </Styled.SliderContainer>
    )
}

export default MarketGuideWidgetCards
